import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { connect } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';

import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import { bePatchStylistBio, ibeStylistBioRead } from 'actions/stylist/beStylistBio.action';
import { feSetStylistId, feSidebarClose } from 'actions/ui.actions';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import TypedOption from 'components/form/TypedOption.component';
import Icon from 'components/generic/Icon.component';
import useAfn from 'hooks/useAfn.hook';
import { usePrepareFetcher, usePrepareReadWrite } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { isEnvMobile } from 'utils/env/determine.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import getCroppedImg from 'utils/image/getCroppedImg.util';
import logger from 'utils/logger.util';

import CN from './ProfileImageUploader.module.scss';

const L = logger('ProfileImageUploader'); // eslint-disable-line no-unused-vars

const ProfileImageUploader = ({
  onClose,
  onSave,
  stylistId,
  initialImage,
  stylistName,
  patchStylistBio,
  abeStylists,
  feSidebarClose,
  feSetStylistId,
}) => {
  const fileRef = useRef(null);
  const isMobile = isEnvMobile();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [imageSrc, setImageSrc] = useState(initialImage || null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [bioText, setBioText] = useState('');

  const { val: isEditableImage, yay: keepEditableImage, nay: discardEditableImage } = useTip(false);
  const { val: removeImage, yay: keepImage, nay: discardImage } = useTip(false);

  const prepStylistRead = usePrepareFetcher(ibeStylistBioRead, { stylistId });
  const resultStylistRead = useQuery(prepStylistRead);

  const { qj } = usePrepareReadWrite({
    reader: ibeStylistBioRead,
    rwOptions: { stylistId },
  });

  const [enableCheck, setEnableCheck] = useState(qj?.showProfileImageInNcob);

  const onCropComplete = useCallback((_, croppedPixels) => void setCroppedAreaPixels(croppedPixels), []);

  const onFileChange = useCallback(
    (ev) => {
      const file = ev.target.files[0];
      if (file) {
        fileRef.current = file;
        discardImage();
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc);
        }
        const newUrl = URL.createObjectURL(file);
        setImageSrc(newUrl);

        keepEditableImage();
      }
    },
    [discardImage, imageSrc, keepEditableImage],
  );

  const handleRemoveImage = useCallback(() => {
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc);
      setImageSrc(null);

      keepEditableImage();
      fileRef.current = null;
      keepImage();
    }
  }, [imageSrc, keepEditableImage, keepImage]);

  const [resultSave, handleSave] = useAfn(async () => {
    L.debug('handleSave()', 'fileRef.current', fileRef.current, 'typeof:', typeof fileRef.current);
    let croppedFile = null;

    if (fileRef.current && croppedAreaPixels) {
      try {
        const previewUrl = URL.createObjectURL(fileRef.current);
        croppedFile = await getCroppedImg(previewUrl, croppedAreaPixels, rotation);
        URL.revokeObjectURL(previewUrl);
      } catch (e) {
        L.warn('handleSave()', 'Cropping error:', e);
      }
    }

    const requestOptions = {
      stylistId,
      bio: bioText,
      show_profile_image_in_ncob: enableCheck,
    };
    if (removeImage) {
      requestOptions.profile_image = null;
    } else if (croppedFile) {
      requestOptions.profile_image = croppedFile;
    }

    L.debug('handleSave()', 'requestOptions:', requestOptions, 'removeImage', removeImage, 'PATCH payload', {
      stylistId,
      bio: bioText,
      enableCheck,
      profile_image: croppedFile,
    });

    const result = await patchStylistBio(requestOptions);

    if (result.is.ok) {
      const updatedStylist = result.json?.results;
      if (updatedStylist) {
        abeStylists();
        await resultStylistRead.refetch();
        onClose();
        feSetStylistId(null);
        feSidebarClose({ tag: 'ProfileImageUploader.handleSave()' });
      }
      call('ProfileImageUploader.onSave', onSave);
    } else {
      L.warn('handleSave()', 'result:', result);
    }
  }, [
    croppedAreaPixels,
    stylistId,
    bioText,
    enableCheck,
    removeImage,
    patchStylistBio,
    rotation,
    onSave,
    abeStylists,
    resultStylistRead,
    onClose,
    feSetStylistId,
    feSidebarClose,
  ]);

  const handleToggleCheckbox = useCallback(() => void setEnableCheck((prev) => !prev), []);
  const handleNoteChange = useCallback((ev) => void setBioText(fromAnyToString(ev?.raw)), []);

  useEffect(() => {
    if (!qj) return;
    if (fromAnyToString(qj.bio)) setBioText(qj.bio);
    if (fromAnyToBool(qj.showProfileImageInNcob)) setEnableCheck(qj.showProfileImageInNcob);
  }, [qj]);

  useEffect(() => {
    if (initialImage) {
      discardEditableImage();
    }
  }, [discardEditableImage, initialImage]);

  useMemo(() => void L.debug('()', { qj, croppedAreaPixels, resultSave }), [croppedAreaPixels, qj, resultSave]);

  return (
    <div
      data-bem="ProfileImageUploader"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="ProfileImageUploader__header" className={CN.modalHeader}>
        {stylistName ?? t('scheduler.header.stylistProfileImagePart1')} {t('scheduler.header.stylistProfileImagePart2')}
        <div className={CN.closeButtonContainer}>
          <Button variant="empty" onClick={onClose}>
            <Icon variant="close" />
          </Button>
        </div>
      </div>
      <div
        data-bem="ProfileImageUploader__body"
        className={cn({
          [CN.modalBody]: true,
          [CN.listView]: imageSrc,
        })}
      >
        <div
          className={cn({
            [CN.gridView]: true,
            [CN.listView]: imageSrc,
          })}
        >
          {imageSrc ? (
            <div
              className={cn({
                [CN.cropperContainer]: true,
                [CN.currentImagePreview]: !isEditableImage,
              })}
            >
              <div className={CN.cropArea}>
                {isEditableImage ? (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                ) : (
                  <img src={imageSrc} alt="Stylist profile preview" className={CN.notEditableImagePreview} />
                )}
              </div>
              <div>
                {isEditableImage && <div className={CN.cropAreaTitle}>{t('scheduler.header.adjustSize')}</div>}
                <div
                  className={cn({
                    [CN.container]: true,
                    [CN.currentImagePreview]: !isEditableImage,
                  })}
                >
                  {isEditableImage && (
                    <div className={CN.controls}>
                      <div className={CN.controlGroup}>
                        <label className={CN.controlLabel}>
                          <Icon variant="minusV2" width={isMobile ? 'lg' : 'md'} selected />
                          <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className={CN.zoomRange}
                          />
                          <Icon variant="plusV2" width={isMobile ? 'lg' : 'md'} selected />
                        </label>
                      </div>

                      <div className={CN.controlGroup}>
                        <label className={CN.controlLabel}>
                          <Icon variant="arrowRotate" width={isMobile ? 'lg' : 'md'} selected />
                          <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            onChange={(e) => setRotation(Number(e.target.value))}
                            className={CN.rotationRange}
                          />
                          <Icon variant="arrowRotate" width={isMobile ? 'lg' : 'md'} selected />
                        </label>
                      </div>
                    </div>
                  )}
                  <label className={CN.uploadLabel}>
                    {t('scheduler.header.changeImage')}
                    <input type="file" accept="image/*" onChange={onFileChange} className={CN.hiddenFileInput} />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className={CN.noImagePreview}>
              <Icon variant="user" width="l3" selected variantInvertDark />
            </div>
          )}

          {!imageSrc && (
            <label className={CN.uploadLabel}>
              {t('scheduler.header.uploadImage')}
              <input type="file" accept="image/*" onChange={onFileChange} className={CN.hiddenFileInput} />
            </label>
          )}
        </div>

        <div className={CN.bioContainer}>
          <div
            data-bem="ProfileImageUploader__checkbox"
            className={CN.checkboxContainer}
            role="button"
            onClick={handleToggleCheckbox}
            tabIndex={0}
          >
            <TypedOption
              type="simple-checkbox"
              name="ProfileImageUploader__TypedOption"
              checked={enableCheck}
              onCheck={alwaysNoop}
            ></TypedOption>

            <span className={CN.checkboxLabel}>{t('scheduler.header.displayOnline')}</span>
          </div>
          <div className={CN.notesLabel}>{t('scheduler.header.bioLabel')}</div>

          <Note
            autoscroll
            hasExternalValue
            //  variantInvert
            height="lg"
            name="note"
            onChange={handleNoteChange}
            tag="ProfileImageUploader__note"
            value={bioText}
            placeholder={t('scheduler.header.bioNote')}
          />
        </div>
      </div>
      <div
        data-bem="ProfileImageUploader__footer"
        className={cn({
          [CN.modalFooter]: true,
          [CN.rowView]: imageSrc,
        })}
      >
        {imageSrc && (
          <Button variant="warning" onClick={handleRemoveImage} noMargin>
            <div className={CN.underline}>{t('scheduler.header.removeImageButton')}</div>
          </Button>
        )}
        <Button variant="primaryForce" onClick={handleSave} xSpace disabled={resultSave.isLoading}>
          {resultSave.isLoading
            ? t('scheduler.header.savingButton')
            : imageSrc
              ? t('scheduler.header.doneButton')
              : t('scheduler.header.saveChangesButton')}
        </Button>
      </div>
    </div>
  );
};

const ProfileImageUploaderHoc = connect(
  null,
  mapActionsToDispatchToProps({
    patchStylistBio: bePatchStylistBio,
    abeStylists,
    feSidebarClose,
    feSetStylistId,
  }),
)(ProfileImageUploader);

export default ProfileImageUploaderHoc;
